import { estRefusAutoplay, reprendreAuPremierGeste } from '../utils/audioGesture';

/**
 * Lecture de l'audio d'une réplique, avec atténuation de la musique (CCT-226).
 *
 * POURQUOI UN HOOK À PART, ET PAS DANS L'ÉCRAN
 * --------------------------------------------
 * Trois responsabilités s'y croisent — jouer, atténuer, relever — et chacune doit être
 * défaite proprement dans TOUS les cas de sortie : fin naturelle, saut de l'utilisateur,
 * changement de scène, démontage. Éparpillées dans le rendu, il en reste toujours une qui
 * s'oublie, et l'oubli s'entend : une musique qui reste basse pour le reste de la partie.
 *
 * ⚠️ LA MUSIQUE EST RELEVÉE DANS LE NETTOYAGE DE L'EFFET, jamais dans le gestionnaire de fin.
 * Un `onended` ne se déclenche pas quand l'utilisateur saute la réplique ou quitte l'écran —
 * exactement les cas où la musique resterait sourde sans que personne comprenne pourquoi.
 *
 * ⚠️ AUCUNE ERREUR NE REMONTE. Un audio manquant, un navigateur qui refuse la lecture
 * automatique, un réseau coupé : la scène doit continuer à se lire. Le son est un
 * enrichissement, pas une condition.
 */

import React, { useEffect, useRef } from 'react';

import { duckMusic, followMute } from './useMissionMusic';

/** Base publique des médias d'histoire — même conteneur que les autres assets de scène. */
const BASE_AUDIO = 'https://ccpubmedia.blob.core.windows.net/stories';

/**
 * Debit de restitution.
 *
 * ⚠️ ICI, ET NON A LA GENERATION. Le moteur v3 IGNORE le reglage de vitesse (mesure : ~4,66 s
 * a 0,7 contre ~4,55 s a 1,2 sur le meme texte), et v2 le plafonne a 1,2. Le navigateur, lui,
 * accelere n'importe quel flux — sans rien regenerer, donc sans rien refacturer, et de facon
 * REVERSIBLE : changer ce nombre ne coute rien, la ou une regeneration coute une scene.
 *
 * `preservesPitch` maintient la hauteur de voix : sans lui, accelerer transforme le
 * personnage en dessin anime.
 */
// ⚠️ 1,3 ALTERE LA VOIX (constate a l'ecoute). L'etirement temporel du navigateur reconstruit
// le signal : plus on pousse, plus il invente. Et la degradation n'est PAS uniforme — la voix
// de Marc reste nette la ou d'autres se metallisent, ce qui dit que le defaut vient de la
// qualite d'entrainement des voix, pas du debit seul. Accelerer davantage abimerait les voix
// fragiles pour compenser leur faiblesse : c'est le mauvais levier.
export const VITESSE_LECTURE = 1.15;

export function urlAudio(cle: string): string {
  const c = (cle || '').trim().replace(/^\/+/, '');
  if (!c) return '';
  return /^https?:\/\//i.test(c) ? c : `${BASE_AUDIO}/${c}`;
}

/**
 * Joue l'audio d'une réplique tant que le composant est monté.
 *
 * @param cle      clé blob de l'audio, ou rien (le beat est alors muet)
 * @param termine  appelé à la fin de la lecture — permet d'enchaîner sur la durée RÉELLE
 *                 plutôt que sur une estimation à partir du nombre de caractères
 */
/**
 * Marge accordee au-dela de la duree attendue avant de considerer la lecture perdue.
 *
 * Assez large pour absorber un demarrage lent ou une mise en tampon, assez courte pour que
 * l'screen ne reste pas bloque plus de quelques secondes.
 */
const WATCHDOG_MARGIN_MS = 4000;

/**
 * Delai au-dela duquel une lecture est consideree perdue.
 *
 * Extrait en fonction PURE pour etre testable : verifier ce calcul par expression reguliere
 * sur le source ne prouve rien — la premiere version du test cherchait les jetons de la
 * formule, et passait au vert alors que le minuteur n'etait plus arme du tout.
 */
export function delaiVeille(expectedDuration: number, vitesse = VITESSE_LECTURE): number {
  if (!(expectedDuration > 0)) return 0;
  return (expectedDuration * 1000) / (vitesse || 1) + WATCHDOG_MARGIN_MS;
}

export function useLineAudio(
  cle: string | undefined,
  termine?: () => void,
  echecRef?: React.MutableRefObject<boolean>,
  expectedDuration = 0,
): void {
  const finRef = useRef(termine);
  finRef.current = termine;
  const echecInterne = useRef(false);
  const echec = echecRef ?? echecInterne;

  useEffect(() => {
    let annulerReprise: (() => void) | null = null;
    const url = urlAudio(cle || '');
    if (!url || typeof Audio === 'undefined') return;

    let vivant = true;
    echec.current = false;
    let restoreMusic: (() => void) | null = null;
    // ⚠️ LA SOURDINE DU HEADER NE COUPAIT QUE LA MUSIQUE. Cet élément-ci n'a aucun lien avec le
    // lecteur musical : sans abonnement, couper le son en pleine scène laissait les
    // personnages parler. On s'abonne, et on se désabonne au nettoyage — sinon chaque réplique
    // jouée laisserait son élément dans l'ensemble pour toute la session.
    let unfollowMute: (() => void) | null = null;
    let audio: HTMLAudioElement | null = null;
    let veille: ReturnType<typeof setTimeout> | null = null;

    try {
      audio = new Audio(url);
      audio.playbackRate = VITESSE_LECTURE;
      // Nom prefixe selon les moteurs ; l'absence de la propriete ne doit pas casser la lecture.
      const a = audio as any;
      if ('preservesPitch' in a) a.preservesPitch = true;
      if ('mozPreservesPitch' in a) a.mozPreservesPitch = true;
      if ('webkitPreservesPitch' in a) a.webkitPreservesPitch = true;
      unfollowMute = followMute(audio);   // avant `play()` : une voix qui démarre en sourdine reste muette
      restoreMusic = duckMusic();
      // ⚠️ LA MUSIQUE REMONTE A LA FIN DU SON, PAS AU DEMONTAGE DU BEAT.
      //
      // Premiere version : uniquement dans le nettoyage de l'effet. Or l'avancement est
      // MANUEL — la replique se tait, et le joueur reste sur ce beat le temps de lire et de
      // cliquer. La musique restait a 6 % pendant tout ce temps, c'est-a-dire la majeure
      // partie de l'screen : « les voix fonctionnent mais plus de musique ».
      //
      // On releve donc AUX DEUX endroits. La fonction rendue par `duckMusic` est
      // idempotente (garde `rendu`), donc le nettoyage qui suivra ne decomptera pas deux fois.
      audio.onended = () => {
        if (veille) { clearTimeout(veille); veille = null; }
        restoreMusic?.();
        restoreMusic = null;
        if (vivant) finRef.current?.();
      };
      // ⚠️ UN ÉCHEC DE LECTURE NE DOIT PAS ENCHAÎNER (CCT-226).
      //
      // La première version appelait `termine()` ici : la réplique passait à la suivante
      // INSTANTANÉMENT, pendant que la frappe, elle, restait calée sur la durée de l'audio.
      // Résultat observé — aucun son ET un texte saccadé, chaque beat n'affichant que ses
      // premiers mots avant d'être remplacé. Deux symptômes, une seule cause, et le plus
      // trompeur : l'échec ne se voyait nulle part.
      //
      // On signale, et on laisse la frappe finir au rythme du texte (`echecRef`).
      audio.onerror = () => {
        if (veille) { clearTimeout(veille); veille = null; }
        echec.current = true;
        console.warn('[replique] audio illisible :', url, audio?.error?.code ?? '');
        restoreMusic?.();
        restoreMusic = null;
      };
      // ⚠️ CHIEN DE GARDE — « l'auto play n'est pas precis, parfois il ne passe plus ».
      //
      // Un flux qui CALE ne declenche RIEN : ni `onended`, ni `onerror`. Mise en tampon,
      // onglet mis en veille par le navigateur, reseau qui hoquette — l'element reste la,
      // sans erreur, et l'screen attend un signal qui ne viendra jamais. L'enchainement etant
      // commande par la fin du son, la scene s'arrete net et rien n'explique pourquoi.
      //
      // On borne donc l'attente par la duree ATTENDUE de la replique, ramenee au debit de
      // restitution, plus une marge. Passe ce delai on considere la replique dite : mieux
      // vaut enchainer un peu tot qu'attendre indefiniment.
      const plafond = delaiVeille(expectedDuration);
      if (plafond > 0) {
        veille = setTimeout(() => {
          if (!vivant) return;
          console.warn('[replique] lecture jamais terminee, on enchaine :', url);
          restoreMusic?.();
          restoreMusic = null;
          finRef.current?.();
        }, plafond);
      }
      const p = audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch((e: any) => {
          // ⚠️ UN REFUS D'AUTOPLAY N'EST PAS UN FICHIER MANQUANT, ET LES CONFONDRE COÛTAIT LA
          // VOIX. `echec.current = true` fait enchaîner la scène SANS la réplique : sur un
          // simple F5 — où aucun geste n'a précédé — toutes les voix disparaissaient, et le
          // seul indice était un `console.warn`. On réessaie au premier geste, et la ligne
          // n'est PAS déclarée en échec : la veille reste armée, donc la scène avance de toute
          // façon si le joueur ne touche à rien.
          if (estRefusAutoplay(e)) {
            const media = audio;
            annulerReprise = reprendreAuPremierGeste(() => {
              media?.play().catch(() => { /* second refus : on laisse la veille faire */ });
            });
            return;
          }
          echec.current = true;
          console.warn('[line] playback refused:', url, e?.name, e?.message);
          restoreMusic?.();
          restoreMusic = null;
        });
      }
    } catch {
      restoreMusic?.();
      restoreMusic = null;
    }

    return () => {
      // Sans cette annulation, quitter une scène laisserait un écouteur qui jouerait sa
      // réplique par-dessus l'écran suivant.
      annulerReprise?.();
      vivant = false;
      if (veille) { clearTimeout(veille); veille = null; }
      try {
        if (audio) {
          // ⚠️ ON DETACHE LES GESTIONNAIRES AVANT DE VIDER `src`. Vider la source declenche
          // un `error` sur l'element qu'on abandonne : sans ce detachement, il marquait en
          // echec la replique SUIVANTE, dont l'effet venait tout juste de remettre le
          // drapeau a zero. Un beat sain heritait ainsi du naufrage de son predecesseur.
          audio.onended = null;
          audio.onerror = null;
          audio.pause();
          audio.src = '';        // libère le flux : sans ça le téléchargement continue
        }
      } catch {
        /* best-effort */
      }
      unfollowMute?.();
      unfollowMute = null;
      restoreMusic?.();        // ⚠️ ICI, pas dans `onended` : saut et démontage y passent aussi
      restoreMusic = null;
    };
  }, [cle]);
}
